<template>
  <header class="navbar">
    <!-- Mobile Menu Toggle -->
    <button 
      class="mobile-menu-toggle" 
      @click="$emit('toggle-sidebar')"
      aria-label="Toggle menu"
      v-if="showMobileMenu"
    >
      <i class="fa-solid fa-bars"></i>
    </button>
    
    <!-- LEFT SECTION: Navigation Icons -->
    <div class="left-section">
      <!-- Dashboard Home -->
      <div
        class="nav-icon"
        @click="$emit('change-view', 'OverView')"
        :class="{ active: activeSection === 'OverView' }"
        title="Dashboard"
      >
        <i class="fa-solid fa-house"></i>
        <span class="tooltip">Dashboard</span>
      </div>

      <!-- Messages -->
      <div class="nav-icon" title="Messages">
        <i class="fa-solid fa-comments"></i>
        <span v-if="unreadMessages > 0" class="badge">{{ unreadMessages > 9 ? '9+' : unreadMessages }}</span>
        <span class="tooltip">Messages</span>
      </div>

      <!-- Calendar -->
      <div
        class="nav-icon"
        @click="$emit('change-view', 'AdminCalendar')"
        :class="{ active: activeSection === 'AdminCalendar' }"
        title="Calendar"
      >
        <i class="fa-solid fa-calendar-days"></i>
        <span class="tooltip">Calendar</span>
      </div>

      <!-- Customer support tickets (super_admin / support only); count from AdminSupport -->
      <div
        v-if="canAccessCustomerTickets"
        class="nav-icon"
        @click="$emit('change-view', 'AdminSupport')"
        :class="{ active: activeSection === 'AdminSupport' }"
        title="Customer support tickets"
      >
        <i class="fa-solid fa-life-ring"></i>
        <span v-if="ticketCount > 0" class="badge badge-red">{{ ticketCount > 9 ? '9+' : ticketCount }}</span>
        <span class="tooltip">Customer tickets</span>
      </div>

      <!-- Notifications -->
      <div class="nav-icon dropdown" @click="toggleNotifications" title="Notifications">
        <i class="fa-solid fa-bell"></i>
        <span v-if="unreadNotifications > 0" class="badge badge-yellow">{{ unreadNotifications > 9 ? '9+' : unreadNotifications }}</span>
        <span class="tooltip">Notifications</span>
        
        <!-- Notifications Dropdown -->
        <div v-if="showNotifications" class="dropdown-menu notifications-dropdown">
          <div class="dropdown-header">
            <h4>Notifications</h4>
            <button class="mark-read" @click.stop="markAllAsRead">Mark all as read</button>
          </div>
          <div class="dropdown-content">
            <div v-if="notifications.length === 0" class="empty-notifications">
              <i class="fa-solid fa-bell-slash"></i>
              <p>No new notifications</p>
            </div>
            <div v-else class="notifications-list">
              <div 
                v-for="notification in notifications" 
                :key="notification.id" 
                class="notification-item"
                :class="{ unread: !notification.read }"
              >
                <div class="notification-icon">
                  <i :class="notification.icon"></i>
                </div>
                <div class="notification-content">
                  <p class="notification-text">{{ notification.text }}</p>
                  <span class="notification-time">{{ notification.time }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="dropdown-footer" @click="$emit('change-view', 'Notifications')">
            View All Notifications
          </div>
        </div>
      </div>
    </div>

    <!-- CENTER SECTION: Search Bar -->
    <div class="center-section">
      <div class="search-container">
        <i class="fa-solid fa-magnifying-glass search-icon"></i>
        <input 
          type="text" 
          v-model="searchQuery"
          placeholder="Search for users, products, orders..." 
          class="search-input"
          @keyup.enter="handleSearch"
          @focus="showSearchSuggestions = true"
          @blur="hideSearchSuggestions"
        />
        <button class="search-btn" @click="handleSearch">
          <i class="fa-solid fa-arrow-right"></i>
        </button>
        
        <!-- Search Suggestions -->
        <div v-if="showSearchSuggestions && searchQuery.trim()" class="search-suggestions">
          <div class="suggestion-header">
            <span>Quick Searches</span>
          </div>
          <div class="suggestions-list">
            <div class="suggestion-item" @mousedown="selectSuggestion('Customers')">
              <i class="fa-solid fa-users"></i>
              <span>Customers</span>
            </div>
            <div class="suggestion-item" @mousedown="selectSuggestion('Products')">
              <i class="fa-solid fa-box"></i>
              <span>Products</span>
            </div>
            <div class="suggestion-item" @mousedown="selectSuggestion('Orders')">
              <i class="fa-solid fa-shopping-cart"></i>
              <span>Orders</span>
            </div>
            <div class="suggestion-item" @mousedown="selectSuggestion('Sellers')">
              <i class="fa-solid fa-store"></i>
              <span>Sellers</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- RIGHT SECTION: User & Settings -->
    <div class="right-section">
      <!-- Quick Actions -->
      <div class="quick-actions">
        <button class="action-btn" title="Create New" @click="$emit('create-new')">
          <i class="fa-solid fa-plus"></i>
          <span class="action-text">New</span>
        </button>
        
        <button class="action-btn" title="Quick Settings" @click="showQuickSettings = !showQuickSettings">
          <i class="fa-solid fa-sliders"></i>
          <span class="action-text">Settings</span>
        </button>
      </div>

      <!-- User Profile -->
      <div class="user-profile dropdown" @click="toggleProfileMenu">
        <div class="avatar">
          <img
            :src="userAvatar"
            alt="Admin Avatar"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div class="user-info">
          <span class="user-name">{{ userName }}</span>
          <span class="user-role">Administrator</span>
        </div>
        <i class="fa-solid fa-chevron-down dropdown-arrow"></i>
        
        <!-- Profile Dropdown Menu -->
        <div v-if="showProfileMenu" class="dropdown-menu profile-dropdown">
          <div class="dropdown-header">
            <div class="profile-summary">
              <img
                :src="userAvatar"
                alt="Profile"
                class="dropdown-avatar"
                loading="lazy"
                decoding="async"
              />
              <div>
                <h4>{{ userName }}</h4>
                <p>Administrator</p>
              </div>
            </div>
          </div>
          <div class="dropdown-content">
            <div class="dropdown-item" @click="$emit('change-view', 'AdminProfile')">
              <i class="fa-solid fa-user"></i>
              <span>My Profile</span>
            </div>
            <div class="dropdown-item" @click="$emit('change-view', 'AdminSettings')">
              <i class="fa-solid fa-gear"></i>
              <span>Account Settings</span>
            </div>
            <div class="dropdown-item" @click="$emit('change-view', 'ActivityLog')">
              <i class="fa-solid fa-clock-rotate-left"></i>
              <span>Activity Log</span>
            </div>
            <div class="dropdown-divider"></div>
            <div class="dropdown-item logout" @click="handleLogout">
              <i class="fa-solid fa-right-from-bracket"></i>
              <span>Logout</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Theme Toggle -->
      <button 
        class="theme-toggle" 
        @click="toggleTheme"
        :title="theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'"
      >
        <transition name="fade" mode="out-in">
          <i v-if="theme === 'light'" key="moon" class="fa-solid fa-moon"></i>
          <i v-else key="sun" class="fa-solid fa-sun"></i>
        </transition>
      </button>
    </div>
  </header>
</template>

<script>
import { useThemeStore } from "../../themestores/theme";
import { useAdminAuthStore } from "../../authen/adminAuth";
import { mapStores, mapState, mapActions } from "pinia";

export default {
  name: "AdminNavbar",
  props: {
    ticketCount: {
      type: Number,
      default: 0,
    },
    activeSection: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      searchQuery: "",
      showSearchSuggestions: false,
      showNotifications: false,
      showProfileMenu: false,
      showQuickSettings: false,
      unreadMessages: 3,
      unreadNotifications: 5,
      notifications: [
        { id: 1, icon: 'fa-solid fa-cart-plus', text: 'New order #ORD-7842 received', time: '5 min ago', read: false },
        { id: 2, icon: 'fa-solid fa-user-plus', text: 'New customer registered', time: '1 hour ago', read: true },
        { id: 3, icon: 'fa-solid fa-star', text: 'Product review added', time: '2 hours ago', read: false },
        { id: 4, icon: 'fa-solid fa-truck', text: 'Order #ORD-7810 shipped', time: '5 hours ago', read: true },
        { id: 5, icon: 'fa-solid fa-exclamation-circle', text: 'Support ticket #TKT-452 updated', time: '1 day ago', read: false },
      ],
      userName: "Admin User",
      userAvatar: "https://ui-avatars.com/api/?name=Admin+User&background=6366f1&color=fff&bold=true",
      showMobileMenu: false,
    };
  },
  computed: {
    ...mapStores(useThemeStore),
    ...mapState(useThemeStore, ["theme"]),
    canAccessCustomerTickets() {
      const adminStore = useAdminAuthStore();
      adminStore.checkToken();
      return adminStore.canAccessSupportAdminTools;
    },
  },
  methods: {
    ...mapActions(useThemeStore, ["toggleTheme"]),
    
    handleSearch() {
      if (this.searchQuery.trim()) {
        this.$emit('search', this.searchQuery);
        this.showSearchSuggestions = false;
      }
    },
    
    selectSuggestion(type) {
      this.searchQuery = type;
      this.handleSearch();
    },
    
    hideSearchSuggestions() {
      setTimeout(() => {
        this.showSearchSuggestions = false;
      }, 200);
    },
    
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      this.showProfileMenu = false;
      if (this.showNotifications) {
        this.markAllAsRead();
      }
    },
    
    markAllAsRead() {
      this.notifications.forEach(notification => {
        notification.read = true;
      });
      this.unreadNotifications = 0;
    },
    
    toggleProfileMenu() {
      this.showProfileMenu = !this.showProfileMenu;
      this.showNotifications = false;
    },
    
    handleLogout() {
      if (confirm("Are you sure you want to logout?")) {
        this.$emit('logout');
        this.showProfileMenu = false;
      }
    },
    
    updateMobileMenu() {
      this.showMobileMenu = window.innerWidth <= 768;
    },
  },
  mounted() {
    // Check screen size on mount
    this.updateMobileMenu();
    window.addEventListener('resize', this.updateMobileMenu);
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.dropdown')) {
        this.showNotifications = false;
        this.showProfileMenu = false;
        this.showSearchSuggestions = false;
      }
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateMobileMenu);
  },
};
</script>

<style scoped>
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background: var(--background-color2);
  border-bottom: 1px solid var(--background-color3);
  box-shadow: 0 2px 15px var(--box-shadow);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
}

/* Mobile Menu Toggle */
.mobile-menu-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  cursor: pointer;
  margin-right: 0.75rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.mobile-menu-toggle:hover {
  background: var(--background-color3);
  color: var(--border-color-hover);
}

.mobile-menu-toggle i {
  font-size: 1.1rem;
}

/* Common flex container */
.left-section,
.center-section,
.right-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* LEFT SECTION: Navigation Icons */
.left-section {
  flex: 0 0 auto;
}

.nav-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--text-color2);
}

.nav-icon:hover {
  background: var(--background-color1);
  color: var(--border-color-hover);
  transform: translateY(-2px);
}

.nav-icon.active {
  background: var(--border-color-hover1);
  color: var(--border-color-hover);
}

.nav-icon i {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}

.nav-icon:hover i {
  transform: scale(1.1);
}

/* Badge for notifications */
.badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: var(--primary-color);
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 2s infinite;
}

.badge-red {
  background: #ef4444;
}

.badge-yellow {
  background: #f59e0b;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
  70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}

/* Tooltip */
.tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(8px);
  background: var(--background-color2);
  color: var(--text-color1);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--background-color3);
  z-index: 1000;
}

.nav-icon:hover .tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(4px);
}

/* CENTER SECTION: Search Bar */
.center-section {
  flex: 1;
  max-width: 600px;
  margin: 0 2rem;
}

.search-container {
  position: relative;
  flex: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  background: var(--background-color1);
  border: 2px solid var(--background-color3);
  border-radius: 12px;
  color: var(--text-color1);
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px var(--border-color-hover1);
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
  font-size: 1rem;
  transition: color 0.3s ease;
}

.search-input:focus + .search-icon {
  color: var(--border-color-hover);
}

.search-btn {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  background: var(--border-color-hover);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.search-btn:hover {
  background: var(--border-color-hover3);
  transform: translateY(-50%) scale(1.05);
}

/* Search Suggestions */
.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--background-color2);
  border: 1px solid var(--background-color3);
  border-radius: 12px;
  margin-top: 0.5rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.suggestion-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--background-color3);
  font-size: 0.8rem;
  color: var(--text-color2);
  font-weight: 500;
}

.suggestions-list {
  padding: 0.5rem;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-color1);
}

.suggestion-item:hover {
  background: var(--background-color1);
  transform: translateX(4px);
}

.suggestion-item i {
  color: var(--border-color-hover);
  font-size: 0.9rem;
}

/* RIGHT SECTION: User & Settings */
.right-section {
  flex: 0 0 auto;
}

.quick-actions {
  display: flex;
  gap: 0.5rem;
  margin-right: 1rem;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color2);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: var(--background-color3);
  color: var(--border-color-hover);
  transform: translateY(-2px);
}

.action-text {
  font-weight: 500;
}

/* User Profile */
.user-profile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.25rem 0.75rem 0.25rem 0.25rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.user-profile:hover {
  background: var(--background-color1);
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid var(--border-color-hover1);
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color1);
}

.user-role {
  font-size: 0.75rem;
  color: var(--text-color2);
}

.dropdown-arrow {
  font-size: 0.8rem;
  color: var(--text-color2);
  transition: transform 0.3s ease;
}

.user-profile:hover .dropdown-arrow {
  color: var(--border-color-hover);
  transform: rotate(180deg);
}

/* Theme Toggle */
.theme-toggle {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin-left: 0.5rem;
}

.theme-toggle:hover {
  background: var(--background-color3);
  color: var(--border-color-hover);
  transform: rotate(20deg) scale(1.1);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Dropdown Styles */
.dropdown {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: var(--background-color2);
  border: 1px solid var(--background-color3);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  z-index: 1001;
  animation: slideDown 0.2s ease;
}

.notifications-dropdown {
  min-width: 350px;
}

.dropdown-header {
  padding: 1rem;
  border-bottom: 1px solid var(--background-color3);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dropdown-header h4 {
  margin: 0;
  color: var(--text-color1);
  font-size: 1rem;
}

.mark-read {
  background: none;
  border: none;
  color: var(--border-color-hover);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;
}

.mark-read:hover {
  text-decoration: underline;
}

.dropdown-content {
  max-height: 300px;
  overflow-y: auto;
}

.empty-notifications {
  padding: 2rem 1rem;
  text-align: center;
  color: var(--text-color2);
}

.empty-notifications i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.empty-notifications p {
  margin: 0;
  font-size: 0.9rem;
}

.notifications-list {
  padding: 0.5rem;
}

.notification-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.notification-item:hover {
  background: var(--background-color1);
}

.notification-item.unread {
  background: var(--border-color-hover1);
}

.notification-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--background-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--border-color-hover);
  flex-shrink: 0;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-text {
  margin: 0 0 0.25rem 0;
  font-size: 0.85rem;
  color: var(--text-color1);
  line-height: 1.4;
}

.notification-time {
  font-size: 0.75rem;
  color: var(--text-color2);
}

.dropdown-footer {
  padding: 1rem;
  border-top: 1px solid var(--background-color3);
  text-align: center;
  color: var(--border-color-hover);
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.dropdown-footer:hover {
  background: var(--background-color1);
}

/* Profile Dropdown */
.profile-summary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.dropdown-avatar {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  border: 2px solid var(--border-color-hover1);
}

.profile-summary h4 {
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  color: var(--text-color1);
}

.profile-summary p {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-color2);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;
  margin: 0.25rem 0.5rem;
}

.dropdown-item:hover {
  background: var(--background-color1);
  color: var(--border-color-hover);
  transform: translateX(4px);
}

.dropdown-item i {
  width: 20px;
  text-align: center;
  font-size: 0.9rem;
}

.dropdown-divider {
  height: 1px;
  background: var(--background-color3);
  margin: 0.5rem 1rem;
}

.dropdown-item.logout {
  color: #ef4444;
}

.dropdown-item.logout:hover {
  background: rgba(239, 68, 68, 0.1);
}

/* RESPONSIVE DESIGN */

/* Large tablets and small desktops (1024px and below) */
@media (max-width: 1024px) {
  .navbar {
    padding: 0.75rem 1.25rem;
  }
  
  .center-section {
    margin: 0 1rem;
    max-width: 500px;
  }
  
  .search-input {
    font-size: 0.85rem;
    padding: 0.65rem 0.9rem 0.65rem 2.75rem;
  }
  
  .action-text {
    display: none;
  }
  
  .action-btn {
    padding: 0.5rem;
    min-width: 40px;
  }
  
  .user-info {
    display: none;
  }
  
  .user-profile {
    padding: 0.25rem 0.5rem 0.25rem 0.25rem;
  }
}

/* Tablets (768px and below) */
@media (max-width: 768px) {
  .navbar {
    padding: 0.75rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .mobile-menu-toggle {
    display: flex;
  }
  
  .left-section {
    flex: 1 1 auto;
    min-width: 0;
  }
  
  .center-section {
    display: none;
  }
  
  .right-section {
    flex: 0 0 auto;
  }
  
  .quick-actions {
    margin-right: 0.5rem;
    gap: 0.375rem;
  }
  
  .action-btn {
    padding: 0.5rem;
    min-width: 38px;
  }
  
  .user-profile {
    padding: 0.25rem;
  }
  
  .dropdown-menu {
    right: -50px;
    min-width: 260px;
  }
  
  .notifications-dropdown {
    min-width: 320px;
    right: -30px;
  }
  
  .nav-icon {
    width: 38px;
    height: 38px;
  }
  
  .nav-icon i {
    font-size: 1rem;
  }
}

/* Mobile phones (640px and below) */
@media (max-width: 640px) {
  .navbar {
    padding: 0.625rem 0.75rem;
  }
  
  .left-section {
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  
  .nav-icon {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
  }
  
  .nav-icon i {
    font-size: 0.95rem;
  }
  
  .badge {
    min-width: 16px;
    height: 16px;
    font-size: 0.65rem;
    top: -3px;
    right: -3px;
  }
  
  .quick-actions {
    margin-right: 0.375rem;
    gap: 0.25rem;
  }
  
  .action-btn {
    padding: 0.5rem;
    min-width: 36px;
    width: 36px;
    height: 36px;
  }
  
  .action-btn i {
    font-size: 0.9rem;
  }
  
  .user-profile {
    padding: 0.25rem;
  }
  
  .avatar {
    width: 36px;
    height: 36px;
  }
  
  .theme-toggle {
    width: 36px;
    height: 36px;
    margin-left: 0.375rem;
  }
  
  .theme-toggle i {
    font-size: 0.9rem;
  }
  
  .dropdown-menu {
    min-width: 240px;
    right: -60px;
    max-width: calc(100vw - 20px);
  }
  
  .notifications-dropdown {
    min-width: 280px;
    right: -40px;
    max-width: calc(100vw - 20px);
  }
  
  .dropdown-header {
    padding: 0.875rem;
  }
  
  .dropdown-header h4 {
    font-size: 0.95rem;
  }
  
  .dropdown-item {
    padding: 0.625rem 0.875rem;
    font-size: 0.9rem;
  }
  
  .notification-item {
    padding: 0.625rem;
  }
  
  .notification-text {
    font-size: 0.8rem;
  }
  
  .notification-time {
    font-size: 0.7rem;
  }
}

/* Small mobile phones (480px and below) */
@media (max-width: 480px) {
  .navbar {
    padding: 0.5rem 0.625rem;
  }
  
  .mobile-menu-toggle {
    width: 36px;
    height: 36px;
    margin-right: 0.5rem;
  }
  
  .mobile-menu-toggle i {
    font-size: 1rem;
  }
  
  .left-section {
    gap: 0.375rem;
  }
  
  .nav-icon {
    width: 34px;
    height: 34px;
  }
  
  .nav-icon i {
    font-size: 0.9rem;
  }
  
  .tooltip {
    display: none; /* Hide tooltips on very small screens */
  }
  
  .quick-actions {
    margin-right: 0.25rem;
    gap: 0.25rem;
  }
  
  .action-btn {
    width: 34px;
    height: 34px;
    min-width: 34px;
    padding: 0.375rem;
  }
  
  .action-btn i {
    font-size: 0.85rem;
  }
  
  .avatar {
    width: 34px;
    height: 34px;
  }
  
  .theme-toggle {
    width: 34px;
    height: 34px;
    margin-left: 0.25rem;
  }
  
  .theme-toggle i {
    font-size: 0.85rem;
  }
  
  .dropdown-menu {
    min-width: 220px;
    right: -70px;
    max-width: calc(100vw - 16px);
  }
  
  .notifications-dropdown {
    min-width: 260px;
    right: -50px;
    max-width: calc(100vw - 16px);
  }
  
  .dropdown-header {
    padding: 0.75rem;
  }
  
  .dropdown-header h4 {
    font-size: 0.9rem;
  }
  
  .mark-read {
    font-size: 0.75rem;
  }
  
  .dropdown-content {
    max-height: 250px;
  }
  
  .dropdown-item {
    padding: 0.625rem 0.75rem;
    font-size: 0.85rem;
    margin: 0.2rem 0.375rem;
  }
  
  .dropdown-item i {
    font-size: 0.85rem;
    width: 18px;
  }
  
  .notification-item {
    padding: 0.5rem;
  }
  
  .notification-icon {
    width: 28px;
    height: 28px;
  }
  
  .notification-text {
    font-size: 0.75rem;
  }
  
  .notification-time {
    font-size: 0.65rem;
  }
  
  .dropdown-footer {
    padding: 0.75rem;
    font-size: 0.8rem;
  }
}

/* Extra small phones (360px and below) */
@media (max-width: 360px) {
  .navbar {
    padding: 0.5rem;
  }
  
  .left-section {
    gap: 0.25rem;
  }
  
  .nav-icon {
    width: 32px;
    height: 32px;
  }
  
  .nav-icon i {
    font-size: 0.85rem;
  }
  
  .badge {
    min-width: 14px;
    height: 14px;
    font-size: 0.6rem;
    padding: 0 3px;
  }
  
  .action-btn {
    width: 32px;
    height: 32px;
    min-width: 32px;
  }
  
  .avatar {
    width: 32px;
    height: 32px;
  }
  
  .theme-toggle {
    width: 32px;
    height: 32px;
  }
  
  .dropdown-menu {
    min-width: 200px;
    right: -80px;
  }
  
  .notifications-dropdown {
    min-width: 240px;
    right: -60px;
  }
}

/* Landscape orientation on mobile */
@media (max-height: 500px) and (orientation: landscape) {
  .navbar {
    padding: 0.5rem 1rem;
  }
  
  .nav-icon {
    width: 36px;
    height: 36px;
  }
  
  .dropdown-menu {
    max-height: calc(100vh - 100px);
  }
  
  .dropdown-content {
    max-height: 200px;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .nav-icon,
  .action-btn,
  .theme-toggle,
  .user-profile {
    min-width: 44px;
    min-height: 44px;
  }
  
  .search-btn {
    min-width: 40px;
    min-height: 40px;
  }
  
  .dropdown-item {
    min-height: 44px;
  }
  
  .suggestion-item {
    min-height: 44px;
  }
  
  /* Increase touch targets */
  .nav-icon:active {
    transform: scale(0.95);
  }
  
  .action-btn:active {
    transform: scale(0.95);
  }
}

/* High DPI displays */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .navbar {
    border-bottom-width: 0.5px;
  }
}
</style>